/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  Button,
  Icon,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Text,
  VisuallyHidden,
} from "@microbit/ui";
import { ReactNode, useCallback, useState } from "react";
import { RiFileCopy2Line, RiGithubFill } from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import { Flex, HStack, styled, VStack } from "styled-system/jsx";
import { EXPECTED_API_VERSION } from "../../device/pico/api-version";
import micropythonLogo from "./micropython.jpeg";
import pythonPoweredLogo from "./python-powered.png";

/**
 * Staende, die dieser Oberflaeche zugrunde liegen.
 *
 * Die MicroPython-Fassung ist die der Roboterfirmware (revisions.lock im
 * Hauptrepository). Die Roboter-API traegt die Nummer, die die Oberflaeche beim
 * Verbinden erwartet.
 */
const versionInfo: {
  name: string;
  nameId?: string;
  value: string;
  href?: string;
}[] = [
  {
    name: "Editor",
    value: import.meta.env.VITE_VERSION,
    href: "https://github.com/microbit-foundation/python-editor-v3",
  },
  {
    name: "MicroPython (Pico 2 W)",
    value: "1.29.0",
    href: "https://github.com/micropython/micropython/releases/tag/v1.29.0",
  },
  {
    name: "Robot API",
    nameId: "about-robot-api",
    value: EXPECTED_API_VERSION,
  },
];

const clipboardVersion = versionInfo
  .map((x) => `${x.name} ${x.value}`)
  .join("\n");

// Minimal clipboard hook (the component library has no equivalent).
const useClipboard = (text: string) => {
  const [hasCopied, setHasCopied] = useState(false);
  const onCopy = useCallback(() => {
    void navigator.clipboard?.writeText(text);
    setHasCopied(true);
    window.setTimeout(() => setHasCopied(false), 1500);
  }, [text]);
  return { hasCopied, onCopy };
};

interface AboutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  finalFocusRef: React.RefObject<HTMLButtonElement>;
}

const cellCss = { borderBottomWidth: "1px", borderColor: "gray.100" };

/**
 * Dialog "Über": wofuer die Oberflaeche da ist, auf wem sie beruht, und die
 * Versionen.
 *
 * Shown via the help menu.
 */
const AboutDialog = ({ isOpen, onClose, finalFocusRef }: AboutDialogProps) => {
  const { hasCopied, onCopy } = useClipboard(clipboardVersion);
  const intl = useIntl();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      finalFocusRef={finalFocusRef}
      aria-label={intl.formatMessage({ id: "about" })}
    >
      <ModalCloseButton />
      <ModalBody>
        <VStack gap="8" pl="5" pr="5" pt="5">
          <HStack gap="4">
            <Flex alignItems="center" justifyContent="flex-end">
              {/* No need to translate */}
              <Image src={micropythonLogo} alt="MicroPython" />
            </Flex>
            <Flex alignItems="center" justifyContent="flex-end">
              <Image
                src={pythonPoweredLogo}
                alt={intl.formatMessage({ id: "python-powered" })}
              />
            </Flex>
          </HStack>

          <Text fontSize="lg" textAlign="center">
            <FormattedMessage id="about-robot" />
          </Text>

          <Text fontSize="lg" textAlign="center">
            <FormattedMessage
              id="about-thanks"
              values={{
                link: (chunks: ReactNode) => (
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    color="brand.500"
                    href="https://python.microbit.org/"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Text>

          <VStack alignItems="center" justifyContent="center" gap="4">
            <styled.table css={{ fontSize: "sm" }}>
              <styled.caption
                color="gray.800"
                css={{ captionSide: "top", py: "1" }}
              >
                <FormattedMessage id="software-versions" />
              </styled.caption>
              <styled.tbody>
                {versionInfo.map((v) => (
                  <styled.tr key={v.name}>
                    <styled.td px="3" py="1" css={cellCss}>
                      {v.nameId ? intl.formatMessage({ id: v.nameId }) : v.name}
                    </styled.td>
                    <styled.td px="3" py="1" css={cellCss}>
                      {v.value}
                    </styled.td>
                    <styled.td p="0" css={cellCss}>
                      {v.href && (
                        // Move padding so we get a reasonable click target.
                        <Link
                          display="block"
                          pl="4"
                          pr="4"
                          pt="2"
                          pb="2"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={v.href}
                        >
                          <Icon as={RiGithubFill} />
                          <VisuallyHidden>GitHub</VisuallyHidden>
                        </Link>
                      )}
                    </styled.td>
                  </styled.tr>
                ))}
              </styled.tbody>
            </styled.table>
            <Button startIcon={<RiFileCopy2Line />} onPress={onCopy} size="md">
              <FormattedMessage id={hasCopied ? "copied" : "copy-action"} />
            </Button>
          </VStack>

          <Text fontSize="lg" textAlign="center">
            <FormattedMessage
              id="about-micropython-robot"
              values={{
                link: (chunks: ReactNode) => (
                  <Link
                    color="brand.500"
                    href="https://micropython.org"
                    target="_blank"
                    rel="noopener"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button onPress={onClose} variant="primary" size="lg">
          <FormattedMessage id="close-action" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AboutDialog;
